import {
  DecorContent,
  ImageContent,
  QuizContent,
  TextContent,
  VideoContent,
} from "./contents"
import { Stack, useMediaQuery, useTheme } from "@mui/material"
import { useRef, type TouchEvent } from "react"
import { baseFontSize } from "./contents/constants"
import { Story, StorySlide } from "../../../models/stories.models"
import Appear from "../../animations/Appear"
import StoryChip from "../StoryChip"
import { colors } from "../../../services/config/colors"

interface StoryContainerProps {
  currentSlide: StorySlide | undefined
  currentSlideIndex: number
  forwardButtonDisabled?: boolean
  goBackward?: () => void
  goForward?: () => void
  story: Story
}

export const StoryContainer = ({
  currentSlide,
  currentSlideIndex,
  forwardButtonDisabled,
  goBackward,
  goForward,
  story,
}: StoryContainerProps) => {
  const theme = useTheme()
  const matchesMD = useMediaQuery(theme.breakpoints.down("md"))

  // get font size and weight based on text length
  const getFontDimensions = (
    text: string
  ): {
    fontSize: string
    fontWeight: 400
  } => {
    const minFontSize = baseFontSize
    const maxFontSize = 25
    const maxLength = 250

    const textLength = text.length

    const clampedLength = Math.min(textLength, maxLength)
    const size =
      maxFontSize - (clampedLength / maxLength) * (maxFontSize - minFontSize)

    return {
      fontSize: `${
        Math.round(Math.max(minFontSize, Math.min(maxFontSize, size))) * 0.063
      }rem`,
      fontWeight: 400,
    }
  }

  // container touch handlers for mobile navigation
  const touchStartX = useRef<number | null>(null)
  const touchStartY = useRef<number | null>(null)
  const touchMoved = useRef(false)

  const containerOnTouchStart = (e: TouchEvent<HTMLDivElement>) => {
    if (matchesMD && goBackward && goForward) {
      touchStartX.current = e.touches[0].clientX
      touchStartY.current = e.touches[0].clientY
      touchMoved.current = false
    }
  }

  const containerOnTouchMove = (e: TouchEvent<HTMLDivElement>) => {
    if (
      matchesMD &&
      goBackward &&
      goForward &&
      touchStartX.current !== null &&
      touchStartY.current !== null
    ) {
      const deltaX = Math.abs(e.touches[0].clientX - touchStartX.current)
      const deltaY = Math.abs(e.touches[0].clientY - touchStartY.current)

      // consider it a scroll if moved more than 10px
      if (deltaX > 10 || deltaY > 10) {
        touchMoved.current = true
      }
    }
  }

  const containerOnTouchEnd = (e: TouchEvent<HTMLDivElement>) => {
    if (
      matchesMD &&
      goBackward &&
      goForward &&
      touchStartX.current !== null &&
      !touchMoved.current
    ) {
      // only navigate if it was a tap (no significant movement)
      if (touchStartX.current <= window.innerWidth * 0.3) {
        e.preventDefault() // prevent synthetic click events
        goBackward()
      } else if (touchStartX.current > window.innerWidth * 0.3) {
        e.preventDefault() // prevent synthetic click events
        goForward()
      }
    }

    // reset tracking
    touchStartX.current = null
    touchStartY.current = null
    touchMoved.current = false
  }

  return (
    <div
      style={{
        width: matchesMD ? "100%" : 375,
        minWidth: matchesMD ? "100%" : 375,
        height: "100%",
        position: "relative",
      }}
    >
      <Stack
        alignItems="center"
        justifyContent="safe center"
        style={{
          width: "100%",
          height: "100%",
          borderRadius: matchesMD ? 0 : 16,
          background: currentSlide?.backgroundColor ?? colors.backgroundWhite,
          overflowY: "auto",
          overflowX: "hidden",
          paddingTop: 48,
          paddingBottom: 48,
          transition: "150ms",
        }}
        onTouchStart={forwardButtonDisabled ? undefined : containerOnTouchStart}
        onTouchMove={forwardButtonDisabled ? undefined : containerOnTouchMove}
        onTouchEnd={forwardButtonDisabled ? undefined : containerOnTouchEnd}
      >
        {/* contents */}
        {!!currentSlide && (
          <Appear
            key={currentSlideIndex}
            scaleStartingValue={0.9}
            fadeDuration={0.2}
            style={{ width: "100%", maxWidth: 500 }}
          >
            <Stack gap={3} style={{ width: "100%" }}>
              {currentSlide.contents.map((content) => {
                if (content.text) {
                  const { fontSize, fontWeight } = getFontDimensions(
                    content.text
                  )

                  return (
                    <TextContent
                      key={content.text}
                      fontSize={fontSize}
                      fontWeight={fontWeight}
                      currentSlide={currentSlide}
                      content={content}
                    />
                  )
                }

                switch (content.media?.type) {
                  case "decor":
                    return (
                      <DecorContent key={content.media.url} content={content} />
                    )
                  case "image":
                    return (
                      <ImageContent key={content.media.url} content={content} />
                    )
                  case "video":
                    return (
                      <VideoContent key={content.media.url} content={content} />
                    )
                }

                if (content.question) {
                  const { fontSize, fontWeight } = getFontDimensions(
                    content.question
                  )

                  return (
                    <QuizContent
                      key={content.question}
                      fontSize={fontSize}
                      fontWeight={fontWeight}
                      currentSlide={currentSlide}
                      content={content}
                    />
                  )
                }
              })}
            </Stack>
          </Appear>
        )}
      </Stack>
      {/* current slide chip */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={{
          width: "calc(100% - 24px)",
          position: "absolute",
          top: 12,
          left: 12,
        }}
      >
        <StoryChip
          current={currentSlideIndex + 1}
          total={story.slides.length}
        />
      </Stack>
    </div>
  )
}
